import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, FormControlLabel, Link, MenuItem, Switch, TextField, Typography } from '@mui/material';
import { fetchYubiqCodes } from '../../utils/projectsService';
import { MUTED } from '../home/homeTheme';
import { ENABLE_TAB_CONTRACT } from '../../config/local.config';

/** Sezioni del progetto: stessa divisione nel pannello di creazione e nelle card della scheda Overview. */
export const PROJECT_SECTIONS = {
  project: { title: 'Project', fields: ['name', 'project_code', 'customer_id', 'tipologia_servizio', 'yubiq_code', 'active'] },
  schedule: { title: 'Schedule', fields: ['start_date', 'end_date'] },
  ticket: { title: 'Ticket ID', fields: ['ticket_id_prefix', 'regex', 'regex_desc'] },
  window: { title: 'Worklog time window', fields: ['days_before_block', 'days_after_block', 'previous_month_blocked'] },
  description: { title: 'Description', fields: ['description'] },
};

/** Yubiq Code disponibili per il cliente scelto (dai Contracts). */
export function useYubiqCodes(customerId) {
  const [codes, setCodes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    setCodes([]);
    fetchYubiqCodes(customerId).then((list) => !cancelled && setCodes(list));
    return () => {
      cancelled = true;
    };
  }, [customerId]);
  return codes;
}

const shrink = { shrink: true };
const monoSx = { '& input': { fontFamily: 'monospace', fontSize: 13.5 } };

/**
 * Campi di una sezione del progetto. onChange(field, value); cambiando cliente lo Yubiq Code si azzera.
 * serviceTypes: voci di ref-lookup contract_type ({ value, label }).
 */
export default function ProjectSectionFields({ section, form, errors, onChange, customers, serviceTypes, onRegexHelp, autoFocus }) {
  const yubiqCodes = useYubiqCodes(section === 'project' && ENABLE_TAB_CONTRACT ? form.customer_id : null);
  const text = (field, label, extra = {}) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      value={form[field] ?? ''}
      onChange={(e) => onChange(field, e.target.value)}
      error={!!errors[field]}
      helperText={errors[field] || extra.helperText}
      InputLabelProps={extra.type === 'date' ? shrink : undefined}
      {...extra}
    />
  );
  const toggle = (field, label, caption) => (
    <FormControlLabel
      sx={{ ml: 0, alignItems: 'flex-start', '& .MuiSwitch-root': { mt: -0.75 } }}
      control={<Switch color="success" checked={!!form[field]} onChange={(e) => onChange(field, e.target.checked)} />}
      label={
        <Box>
          <Typography sx={{ fontSize: 14 }}>{label}</Typography>
          {caption && <Typography sx={{ fontSize: 12, color: MUTED }}>{caption}</Typography>}
        </Box>
      }
    />
  );
  const grid = (children, columns = 2) => <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${columns}, minmax(0, 1fr))` }, gap: 2 }}>{children}</Box>;

  if (section === 'project') {
    const customer = customers.find((c) => String(c.id) === String(form.customer_id)) || null;
    const serviceKnown = !form.tipologia_servizio || serviceTypes.some((o) => o.value === form.tipologia_servizio);
    const yubiqKnown = !form.yubiq_code || yubiqCodes.includes(form.yubiq_code);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {text('name', 'Project name', { autoFocus })}
        {grid(
          <>
            {text('project_code', 'Project code')}
            <Autocomplete
              size="small"
              options={customers}
              value={customer}
              getOptionLabel={(c) => c.name || ''}
              isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
              onChange={(e, c) => onChange('customer_id', c ? c.id : '')}
              renderInput={(params) => (
                <TextField {...params} label="Customer" error={!!errors.customer_id} helperText={errors.customer_id} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} />
              )}
            />
          </>,
        )}
        {ENABLE_TAB_CONTRACT && grid(
          <>
            {text('tipologia_servizio', 'Service type', {
              select: true,
              children: [
                <MenuItem key="" value="">
                  <em>None</em>
                </MenuItem>,
                !serviceKnown && (
                  <MenuItem key={form.tipologia_servizio} value={form.tipologia_servizio}>
                    {form.tipologia_servizio} (inactive)
                  </MenuItem>
                ),
                ...serviceTypes.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label || o.value}
                  </MenuItem>
                )),
              ],
            })}
            {text('yubiq_code', 'Yubiq code', {
              select: true,
              disabled: !form.customer_id,
              helperText: !form.customer_id ? 'Choose the customer first' : !yubiqCodes.length ? 'No Yubiq codes in this customer’s contracts' : undefined,
              children: [
                <MenuItem key="" value="">
                  <em>None</em>
                </MenuItem>,
                !yubiqKnown && (
                  <MenuItem key={form.yubiq_code} value={form.yubiq_code}>
                    {form.yubiq_code}
                  </MenuItem>
                ),
                ...yubiqCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                )),
              ],
            })}
          </>,
        )}
        {toggle('active', 'Active project')}
      </Box>
    );
  }

  if (section === 'schedule') {
    return grid(
      <>
        {text('start_date', 'Start date', { type: 'date' })}
        {text('end_date', 'End date', { type: 'date' })}
      </>,
    );
  }

  if (section === 'ticket') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {grid(
          <>
            {text('ticket_id_prefix', 'Ticket ID prefix', { placeholder: 'e.g. INC' })}
            {text('regex', 'Regex pattern', {
              placeholder: 'e.g. ^INC\\d+$',
              sx: monoSx,
              helperText: errors.regex || (
                <Link component="button" type="button" onClick={onRegexHelp} underline="hover" sx={{ fontSize: 12, fontWeight: 600, color: 'var(--rt-green-dark)' }}>
                  How to write it
                </Link>
              ),
            })}
          </>,
        )}
        {text('regex_desc', 'Regex description', { placeholder: 'Shown to users when a Ticket ID is not valid' })}
      </Box>
    );
  }

  if (section === 'window') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontSize: 12.5, color: MUTED }}>Users can add or edit worklogs within this many days before and after today.</Typography>
        {grid(
          <>
            {text('days_before_block', 'Days before', { type: 'number', inputProps: { min: 1 } })}
            {text('days_after_block', 'Days after', { type: 'number', inputProps: { min: 1 } })}
          </>,
        )}
        {toggle('previous_month_blocked', 'Block previous month', 'Users cannot create or change worklogs for the previous calendar month.')}
      </Box>
    );
  }

  return text('description', 'Description', { multiline: true, minRows: 3, autoFocus });
}
